import styled from "styled-components";
import { Link } from "react-router-dom";

import { pxToRem, withAlpha } from "../../styles/utils";

export const StudentApp = styled.div`
  display: grid;
  grid-template-columns: minmax(${pxToRem(245)}, ${pxToRem(290)}) minmax(0, 1fr);
  min-height: calc(100vh - ${pxToRem(24)});
  padding: ${({ theme }) => pxToRem(theme.spacing.sm)};
  background: ${({ theme }) => theme.colors.pageBg};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  }
`;

export const StudentMain = styled.main`
  min-width: 0;
  padding: ${pxToRem(28)} ${pxToRem(30)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  @media (max-width: 700px) {
    padding: ${({ theme }) => pxToRem(theme.spacing.lg)} ${({ theme }) => pxToRem(theme.spacing.md)};
  }
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
  margin-bottom: ${pxToRem(26)};
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xxl)};
  font-weight: 600;
  line-height: 1.15;
`;

export const PageLead = styled.p`
  max-width: ${pxToRem(720)};
  margin: ${({ theme }) => pxToRem(theme.spacing.xs)} 0 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  line-height: 1.5;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${pxToRem(280)}, 1fr));
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
`;

export const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  min-height: ${pxToRem(170)};
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

export const CardTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 600;
  line-height: 1.3;
`;

export const CardMeta = styled.div`
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const CardText = styled.p`
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.5;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
`;

export const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${pxToRem(10)};
  margin-top: auto;
`;

const linkFocusStyles = `
  border-radius: ${pxToRem(4)};
  outline-offset: ${pxToRem(3)};
`;

export const TextLink = styled(Link)`
  color: ${({ theme }) => theme.colors.brand};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    ${linkFocusStyles}
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
  }
`;

export const ExternalLink = styled.a`
  color: ${({ theme }) => theme.colors.brand};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    ${linkFocusStyles}
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
  }
`;

export const EmptyState = styled.div`
  padding: ${pxToRem(38)};
  border: ${pxToRem(1)} dashed ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  color: ${({ theme }) => theme.colors.inkMuted};
  text-align: center;
`;

export const LoadingState = styled.div`
  padding: ${pxToRem(28)} 0;
  color: ${({ theme }) => theme.colors.inkMuted};
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: ${({ theme }) => pxToRem(theme.spacing.xs)};
  overflow: hidden;
  border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
  background: ${({ theme }) => theme.colors.inkSurface};
`;

export const ProgressFill = styled.div<{ $percent: number }>`
  width: ${({ $percent }) => `${Math.max(0, Math.min(100, $percent))}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ theme }) => theme.colors.brand};
  transition: width 0.3s ease;
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};
`;

export const DetailSection = styled.section`
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const SectionTitle = styled.h2`
  margin: 0 0 ${pxToRem(14)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 600;
`;

export const RichText = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  line-height: 1.6;

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

export const StatusChip = styled.span<{ $tone?: "neutral" | "info" | "warning" | "success" }>`
  display: inline-flex;
  align-items: center;
  min-height: ${pxToRem(28)};
  padding: 0 ${pxToRem(10)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
  background: ${({ $tone, theme }) =>
    $tone === "success"
      ? withAlpha(theme.colors.success, 0.12)
      : $tone === "warning"
        ? withAlpha(theme.colors.warning, 0.14)
        : $tone === "info"
          ? theme.colors.brandSoft
          : theme.colors.inkSurface};
  color: ${({ $tone, theme }) =>
    $tone === "success"
      ? theme.colors.success
      : $tone === "warning"
        ? theme.colors.warningText
        : theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
`;

export const InlineRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${pxToRem(10)};
`;

export const MediaList = styled.div`
  display: grid;
  gap: ${pxToRem(10)};
`;

export const MediaItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.sm)} ${pxToRem(14)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.surfaceCool};
`;

export const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const CalendarDayCard = styled.div`
  min-height: ${pxToRem(118)};
  padding: ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.surfaceMuted};
`;

export const CalendarDate = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ theme }) => theme.colors.ink};
  font-weight: 600;
`;

export const CalendarEvent = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xs)};
  padding-top: ${({ theme }) => pxToRem(theme.spacing.xs)};
  border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.4;
`;
